import { isNumber } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';

import {
    DiagramComponentInstance,
    DiagramConnection,
    isACPortControlMethod,
    isDCPortControlMethod,
    isVoltageControlMethod,
    PortControlMethod,
    PortControlMethods,
    VoltageType,
} from 'models';

type PortIndex = DiagramConnection['from']['port'];

const PortHelpers = {
    /**
     * Normalizes port access for both 0-based (new) and 1-based (legacy) port indexing.
     * Returns an array of [portKey, portData] tuples with normalized 0-based indexing.
     */
    normalizePortEntries: <T>(ports: Record<string, T>): Array<[string, T]> => {
        const portEntries = Object.entries(ports);

        const hasZeroBasedPorts = portEntries.some(([key]) => key === '0');
        const hasOneBasedPorts = portEntries.some(([key]) => key === '1');

        if (hasZeroBasedPorts) {
            return portEntries;
        } else if (hasOneBasedPorts) {
            return portEntries.map(([key, value]) => {
                const numericKey = parseInt(key, 10);
                if (!isNaN(numericKey) && numericKey > 0) {
                    return [(numericKey - 1).toString(), value];
                }
                return [key, value];
            });
        }

        return portEntries;
    },

    /**
     * Gets the display port number (1-based) from a normalized port key (0-based).
     */
    getDisplayPortNumber: (normalizedPortKey: string): number => {
        const numericKey = parseInt(normalizedPortKey, 10);
        return isNaN(numericKey) ? 1 : numericKey + 1;
    },

    getConfiguration: (componentInstance: DiagramComponentInstance, port: number) => {
        return componentInstance.configuration.ports[port];
    },

    getSpecifications: (componentInstance: DiagramComponentInstance, port: number) => {
        return componentInstance.specifications.electrical.ports[port];
    },

    getPower: (componentInstance: DiagramComponentInstance, port: number): number => {
        const voltageType = PortHelpers.getVoltageTypeConfiguration(componentInstance, port);

        if (!voltageType) {
            return 0;
        }

        // @ts-ignore this exists.
        const power = componentInstance.specifications.electrical.ports[port][voltageType].power;

        return power?.nom ?? power?.max ?? 0;
    },

    getNominalVoltage: (componentInstance: DiagramComponentInstance, port: number): number | null => {
        const voltageType = PortHelpers.getVoltageTypeConfiguration(componentInstance, port);

        if (!voltageType) {
            return null;
        }

        // @ts-ignore this exists.
        const voltage = componentInstance.specifications.electrical.ports[port]?.[voltageType]?.voltage;

        return isNumber(voltage?.nom) ? voltage.nom : null;
    },

    getVoltageTypeConfiguration: (componentInstance: DiagramComponentInstance, port: number) => {
        if (!componentInstance.configuration.ports[port]) {
            return null;
        }

        return componentInstance.configuration.ports[port]?.voltageType ?? null;
    },

    getVoltageTypeOptions: (componentInstance: DiagramComponentInstance, port: PortIndex) => {
        const voltageTypes: VoltageType[] = [];

        if (port === null) {
            return voltageTypes;
        }

        const portSpecifications = componentInstance.specifications.electrical.ports[port];

        if (!portSpecifications) {
            // TODO Remove this in a few weeks..
            console.error(`Cannot get PortSpecifications for port ${port}`, JSON.stringify(componentInstance));
        }

        if (portSpecifications && 'AC' in portSpecifications && portSpecifications.AC.enabled) {
            voltageTypes.push('AC');
        }

        if (portSpecifications && 'DC' in portSpecifications && portSpecifications.DC.enabled) {
            voltageTypes.push('DC');
        }

        return voltageTypes;
    },

    isVoltageTypeAllowed: (
        componentInstance: DiagramComponentInstance,
        portIndex: PortIndex,
        voltageType: VoltageType,
    ) => {
        return PortHelpers.getVoltageTypeOptions(componentInstance, portIndex).includes(voltageType);
    },

    getAvailableControlMethods: (componentInstance: DiagramComponentInstance, port: number): PortControlMethod[] => {
        const definition = ComponentInstanceHelpers.definition(componentInstance.componentType);

        const specifications = componentInstance.specifications.electrical.ports[port];
        const configuration = componentInstance.configuration.ports[port];
        const { voltageType } = configuration;

        if (!voltageType) {
            return [];
        }

        if (componentInstance.componentId) {
            // @ts-ignore
            const linkedControlMethods: PortControlMethod[] = specifications[voltageType]?.controlMethods ?? [];

            if (linkedControlMethods.length) {
                return linkedControlMethods;
            }
        }

        const { allowedControlMethods = [] } = definition.ports;

        const checker = {
            AC: isACPortControlMethod,
            DC: isDCPortControlMethod,
        }[voltageType];

        return PortControlMethods.options
            .map((option) => option.value)
            .filter((method) => allowedControlMethods.includes(method) && checker(method));
    },

    canControlVoltage: (componentInstance: DiagramComponentInstance, port: number): boolean => {
        return PortHelpers.getAvailableControlMethods(componentInstance, port).some(isVoltageControlMethod);
    },

    canScale: (componentInstance: DiagramComponentInstance, port: number): boolean => {
        const controlMethod = componentInstance.configuration.ports[port]?.controlMethod ?? null;

        if (controlMethod === 'power-voltage') {
            // During MPPT curtailment the engine scales the tracked current by the PV's own scale instead.
            return !isMaximumPowerPointTrackingConverter(componentInstance);
        }

        if (controlMethod === 'constant-power') {
            return true;
        }

        // The engine runs a solar port without a control method as constant-current.
        return componentInstance.componentType === 'solar' && [null, 'constant-current'].includes(controlMethod);
    },

    parseScale: (value: number | string): number | null => {
        if (typeof value === 'number') return value;

        const parsed = Number(value);

        return value === '' || Number.isNaN(parsed) ? null : parsed;
    },
};

const isMaximumPowerPointTrackingConverter = (componentInstance: DiagramComponentInstance) => {
    const controlMethods = componentInstance.configuration.ports.map((port) => port.controlMethod).sort();

    return (
        componentInstance.componentType === 'converter' &&
        controlMethods.length === 2 &&
        controlMethods[0] === 'maximum-power-point-tracking' &&
        controlMethods[1] === 'power-voltage'
    );
};

export { PortHelpers };
